import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Contact Us</p>
        <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
          Talk to a real career mentor.
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Have a question about a role, hiring plan, or partnership? Drop us a note — we usually reply within a business day.
        </p>
      </div>

      <div className="mt-12 grid lg:grid-cols-12 gap-10">
        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Name *"><Input data-testid="contact-name" value={form.name} onChange={set("name")} required /></Field>
            <Field label="Email *"><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} required /></Field>
            <Field label="Phone *"><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} required /></Field>
            <Field label="Subject *"><Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} required /></Field>
            <Field label="Message *" full><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} required /></Field>
          </div>
          <Button type="submit" data-testid="contact-submit" disabled={sending} size="lg" className="mt-6 rounded-full px-8 h-12 bg-gradient-to-r from-blue-700 to-indigo-600 text-white font-semibold shadow-md shadow-indigo-500/25">
            {sending ? "Sending…" : "Send Message"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </form>

        <div className="lg:col-span-5 space-y-4">
          {[
            { Icon: Mail, label: "Email", value: "hello@careerconnect.io", sub: "For general questions" },
            { Icon: Phone, label: "Phone", value: "+91 90000 00000", sub: "Mon–Sat · 9AM to 7PM IST" },
            { Icon: MapPin, label: "Office", value: "Level 7, Prestige Tower, Bengaluru, IN", sub: "Regional hubs in Pune & Delhi NCR" },
            { Icon: Clock, label: "Business Hours", value: "Mon–Fri · 9:00–19:00", sub: "Sat · 10:00–14:00 · Closed Sunday" },
          ].map((c) => (
            <div key={c.label} data-testid={`contact-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="card-hover bg-white border border-slate-200 rounded-2xl p-5 flex gap-4">
              <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-700 grid place-items-center flex-shrink-0"><c.Icon size={18} /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{c.label}</p>
                <p className="mt-1 text-slate-900 font-semibold">{c.value}</p>
                <p className="text-xs text-slate-500 mt-1">{c.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-sm font-medium text-slate-800">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
